import _ from 'lodash';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import {
  SemanticLegendsQueryWithCategoricalConfigDocument,
  useCreateSemanticLegendWithCategoricalConfigMutation,
  useUpdateSemanticLegendWithCategoricalConfigMutation,
} from '../../../../generated/graphql';
import {combinedFilter} from '../../../../state/graphql/runSelectorQuery';
import {useSelector} from '../../../../state/hooks';
import {usePart, useViewAction} from '../../../../state/views/hooks';
import {Ref as RunSetRef} from '../../../../state/views/runSet/types';
import * as WorkspaceSettingsActions from '../../../../state/views/workspaceSettings/actions';
import {EMPTY_FILTERS} from '../../../../util/filters';
import {logCustomActionToDatadog} from '../../../../util/profiler/report';
import {useWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {useIsSaveWorkspaceAllowed} from '../../../WorkspaceBanners/useWorkspaceBanners';
import {ClearSettingFnType} from '../hooks/useSettingOverrides';
import {
  RunColorOptions,
  SemanticLegendSettingsWithCategoricalConfig,
  YAxisValues,
} from '../types';
import {useMaybeWorkspaceSettingsContext} from '../WorkspaceSettingsContextProvider';
import {SettingType} from './../../../../services/analytics/workspaceSettingsEvents';
import {useRunSetProject} from './../../../../state/views/runSet/hooks';
import {SettingOverrideCounts} from './../../../../state/views/workspaceSettings/settingOverrideUtils';
import {Ref as WorkspaceSettingsRef} from './../../../../state/views/workspaceSettings/types';
import {
  DEFAULT_SEMANTIC_LEGEND_SETTINGS,
  DefaultSemanticLegendSettings,
} from './SemanticLegendDefaults';
import {
  useGrouping,
  useGroupingToastNotification,
} from './useGroupingToastNotification';
import {SEMANTIC_LEGEND_PALETTE_DEFAULT} from './utils/colorPalettes';
import {getColorFromValue} from './utils/getDefaultBucketColors';
import {getMutationVarsFromSettingsWithCategoricalConfig} from './utils/getMutationVarsFromSettings';
import {getRunColorMap} from './utils/getRunColorMap';
import {
  MAX_NUMBER_OF_BUCKETS,
  MIN_NUMBER_OF_BUCKETS,
  MIN_NUMBER_OF_CATEGORIES,
  recalculateSemanticLegendBuckets,
  recalculateSemanticLegendBucketsWithCategoricalConfig,
} from './utils/recalculateSemanticLegendBuckets';
import {useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig} from './utils/useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig';
import {useSemanticLegendSettingsQueryPropsWithCategoricalConfig} from './utils/useSemanticLegendSettingsQueryPropsWithCategoricalConfig';
import {useWorkspaceConfigKeys} from './utils/useWorkspaceConfigKeys';

export function isDefaultSemanticLegendSettings(
  semanticLegendSettings: SemanticLegendSettingsWithCategoricalConfig
) {
  return (
    !semanticLegendSettings ||
    !semanticLegendSettings.key ||
    semanticLegendSettings.key === DEFAULT_SEMANTIC_LEGEND_SETTINGS.key
  );
}

const OPTIMISTIC_UPDATE_FIELDS = [
  'key',
  'yAxisValue',
  'numberOfBuckets',
  'excludedRunColor',
  'bucketCustomizations',
] as const;

type DefaultedSemanticLegendSettingsWithCategoricalConfig =
  SemanticLegendSettingsWithCategoricalConfig & DefaultSemanticLegendSettings;

export type SemanticLegendSettingsWithCategoricalConfigContextType = {
  clearSetting: ClearSettingFnType;
  configKeys: string[];
  hasActiveGrouping: boolean;
  // Whether the semantic legend is metric based colors
  isMetricBasedColors: boolean;
  // Whether the semantic legend spec has previously been created
  specPreviouslyCreated: boolean;
  // The semantic legend run color options - metric based colors or original project colors
  semanticLegendRunColorOptions?: RunColorOptions;
  // The semantic legend settings
  semanticLegendSettings: DefaultedSemanticLegendSettingsWithCategoricalConfig;
  /**
   * These are the settings for the specified settingLevel. This should match what's in the view spec, but not necessarily
   * be the value that is displayed depending on the settingLevel
   */
  settingLevel: 'workspace';
  settingOverrideCounts: SettingOverrideCounts;
  trackSemanticLegendSettings: (
    setting: SettingType,
    value: string | undefined
  ) => void;
  updateSemanticLegendRunColorOptions?: (
    runColorOptions: RunColorOptions
  ) => void;
  /**
   * Updates the semantic legend settings
   */
  updateSemanticLegendSettings?: (
    settings: Partial<SemanticLegendSettingsWithCategoricalConfig>
  ) => void;
  isLoadingBuckets: boolean;
};

export const SemanticLegendSettingsWithCategoricalConfigContext = createContext<
  SemanticLegendSettingsWithCategoricalConfigContextType | undefined
>(undefined);

type SemanticLegendSettingsProps = Pick<
  SemanticLegendSettingsWithCategoricalConfigContextType,
  | 'clearSetting'
  | 'settingLevel'
  | 'settingOverrideCounts'
  | 'trackSemanticLegendSettings'
> & {
  runsetRef: RunSetRef;
  children: React.ReactNode;
};

export const useRunsFilter = (runsetRef: RunSetRef) => {
  const runSet = usePart(runsetRef);
  const filters = usePart(runSet.filtersRef);
  const groupSelections = usePart(runSet.groupSelectionsRef);
  const runsFilter = combinedFilter(
    filters ?? EMPTY_FILTERS,
    groupSelections,
    undefined,
    undefined,
    runSet.search
  );
  return runsFilter;
};

const useUpdateSemanticLegendRunColorOptions = (
  workspaceSettingsRef: WorkspaceSettingsRef
) => {
  // Update the semantic legend run color view action
  const updateSemanticLegendRunColorAction = useViewAction(
    workspaceSettingsRef,
    WorkspaceSettingsActions.updateSemanticLegendRunColorOptions
  );
  // Update the semantic legend run color options
  return useCallback(
    (value: RunColorOptions) => {
      updateSemanticLegendRunColorAction(value);
      const action =
        value === RunColorOptions.KeyBasedColors ? 'enabled' : 'disabled';
      logCustomActionToDatadog(`semanticLegend.${action}`);
    },
    [updateSemanticLegendRunColorAction]
  );
};

export const SemanticLegendSettingsWithCategoricalConfigContextProvider = ({
  children,
  clearSetting,
  runsetRef,
  settingLevel,
  settingOverrideCounts,
  trackSemanticLegendSettings,
}: SemanticLegendSettingsProps) => {
  const {workspaceSettingsRef, viewRef} = useWorkspaceRefsContext();
  const workspaceSettings = usePart(workspaceSettingsRef);
  const getWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig =
    useGetWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig();
  const view = useSelector(state => state.views.views[viewRef.viewID]);
  const canSaveWorkspace = useIsSaveWorkspaceAllowed(view);

  const [localDataUnclamped, setLocalData] =
    useState<SemanticLegendSettingsWithCategoricalConfig>({});

  const semanticLegendRunColorOptions =
    workspaceSettings?.semanticLegendRunColorOptions ??
    RunColorOptions.OriginalProjectColors;

  const runSet = usePart(runsetRef);
  const filters = usePart(runSet.filtersRef);
  const groupSelections = usePart(runSet.groupSelectionsRef);
  const runsFilter = combinedFilter(
    filters ?? undefined,
    groupSelections,
    undefined,
    undefined,
    runSet.search
  );
  const runsSort = usePart(runSet.sortRef);
  const {projectInternalId} = useRunSetProject(runSet.project);
  const {hasActiveGrouping} = useGrouping(runsetRef);

  const configKeys = useWorkspaceConfigKeys(projectInternalId);

  const isKeyBasedColors =
    semanticLegendRunColorOptions === RunColorOptions.KeyBasedColors;

  // Update the semantic legend run color options
  const updateSemanticLegendRunColorOptions =
    useUpdateSemanticLegendRunColorOptions(workspaceSettingsRef);

  const [createSemanticLegendMutation] =
    useCreateSemanticLegendWithCategoricalConfigMutation({
      context: {
        propagateErrors: true,
      },
    });
  const [updateSemanticLegendMutation] =
    useUpdateSemanticLegendWithCategoricalConfigMutation();

  const updateSemanticLegendSettings = useCallback(
    async (
      semanticLegendSettingsData: SemanticLegendSettingsWithCategoricalConfig
    ) => {
      if (
        semanticLegendSettingsData.shouldRecalculateBuckets &&
        semanticLegendSettingsData.key
      ) {
        await recalculateSemanticLegendBucketsWithCategoricalConfig(
          semanticLegendSettingsData,
          JSON.stringify(runsFilter),
          runsSort,
          view?.project?.entityName ?? '',
          view?.project?.name ?? '',
          getWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig(
            semanticLegendSettingsData.yAxisValue
          )
        );
      }
      if (!canSaveWorkspace) {
        setLocalData(prev => ({
          ...prev,
          buckets: semanticLegendSettingsData.buckets,
          selectedCategories: semanticLegendSettingsData.selectedCategories,
        }));
        // skip the actual mutation if the user can't save the workspace
        return;
      }

      // if the semantic legend settings are the default settings or if the view id is not found, we don't want to update the semantic legend settings
      if (
        isDefaultSemanticLegendSettings(semanticLegendSettingsData) ||
        !view?.id
      ) {
        return;
      }

      // update the semantic legend settings
      await updateSemanticLegendMutation({
        variables: {
          id: view?.id,
          currentMetric: semanticLegendSettingsData.key ?? '',
          ...getMutationVarsFromSettingsWithCategoricalConfig(
            semanticLegendSettingsData
          ),
        },
        refetchQueries: [
          {
            query: SemanticLegendsQueryWithCategoricalConfigDocument,
            variables: {id: view?.id ?? ''},
          },
        ],
      });
    },
    [
      view?.id,
      updateSemanticLegendMutation,
      canSaveWorkspace,
      getWorkspaceSettingsSemanticLegendPaletteWithCategoricalConfig,
      runsFilter,
      runsSort,
      view?.project?.entityName,
      view?.project?.name,
    ]
  );

  const createSemanticLegendSettings = useCallback(
    async (
      semanticLegendSettingsData: SemanticLegendSettingsWithCategoricalConfig
    ) => {
      // if the view id is not found, we don't want to create the semantic legend settings
      if (!semanticLegendSettingsData.viewID) {
        console.error('No view ID found');
        return;
      }

      if (!canSaveWorkspace) {
        return;
      }
      try {
        // create the semantic legend settings
        await createSemanticLegendMutation({
          variables: {
            id: semanticLegendSettingsData.viewID,
            ...getMutationVarsFromSettingsWithCategoricalConfig(
              semanticLegendSettingsData
            ),
          },
          refetchQueries: [
            {
              query: SemanticLegendsQueryWithCategoricalConfigDocument,
              variables: {id: semanticLegendSettingsData.viewID ?? ''},
            },
          ],
        });
      } catch (error) {
        console.error('Error creating semantic legend settings', error);
      }
      return;
    },
    [createSemanticLegendMutation, canSaveWorkspace]
  );

  const {
    semanticLegendSettingsData: trueData,
    semanticLegendSettingsLoading,
    specPreviouslyCreated,
    configValuesForRunCategories,
  } = useSemanticLegendSettingsQueryPropsWithCategoricalConfig({
    entityName: view?.project?.entityName ?? '',
    projectName: view?.project?.name ?? '',
    viewID: view?.id ?? '',
    viewName: view?.name ?? '',
    filters: JSON.stringify(runsFilter),
    order: runsSort,
    createSemanticLegendSettings: createSemanticLegendSettings,
    configKeys: configKeys,
    localKey: localDataUnclamped.key,
    localYAxisValue: localDataUnclamped.yAxisValue,
  });

  /**
   * Rules for updating semantic legends
   * 1. You can only create a new spec when it hasn't been created and you have a viewID on the new data
   * 2. You can only update a spec when it's previously been created and you have a viewID. The viewID could be from (a) the just now created spec or (b) the viewID on this page
   */
  function updateRules(
    newSettings: Partial<SemanticLegendSettingsWithCategoricalConfig>,
    viewID: string,
    {
      alreadyCreated,
      isLoading,
    }: {
      alreadyCreated: boolean;
      isLoading: boolean;
    }
  ): [canCreate: boolean, canUpdate: boolean] {
    const canCreate = !isLoading && !alreadyCreated && !!newSettings.viewID;
    const canUpdate = alreadyCreated && !!viewID;
    return [canCreate, canUpdate];
  }

  const update = useCallback(
    async (
      newSettings: Partial<SemanticLegendSettingsWithCategoricalConfig>
    ) => {
      const [canCreate, canUpdate] = updateRules(newSettings, view?.id ?? '', {
        alreadyCreated: specPreviouslyCreated,
        isLoading: semanticLegendSettingsLoading,
      });
      let mutableCanUpdate = canUpdate;

      if (canCreate && canSaveWorkspace) {
        await createSemanticLegendSettings({
          ...DEFAULT_SEMANTIC_LEGEND_SETTINGS,
          viewID: newSettings.viewID,
          ...getMutationVarsFromSettingsWithCategoricalConfig(newSettings),
        });

        // once a spec is created we can update it
        mutableCanUpdate = true;
      }

      updateSemanticLegendSettings(newSettings);
    },
    [
      canSaveWorkspace,
      specPreviouslyCreated,
      createSemanticLegendSettings,
      updateSemanticLegendSettings,
      semanticLegendSettingsLoading,
      view?.id,
    ]
  );

  const debouncedUpdate = useMemo(() => _.debounce(update, 500), [update]);
  const totalNumberOfCategories = trueData.allCategories?.length;
  const yAxisValue = localDataUnclamped.yAxisValue ?? trueData.yAxisValue;

  const localNumberOfCategories =
    localDataUnclamped.numberOfBuckets === undefined ||
    yAxisValue !== YAxisValues.Config_Categories ||
    semanticLegendSettingsLoading
      ? undefined
      : Math.max(
          MIN_NUMBER_OF_CATEGORIES,
          Math.min(
            localDataUnclamped.numberOfBuckets ?? 0,
            totalNumberOfCategories ?? 0,
            MAX_NUMBER_OF_BUCKETS
          )
        );
  const localNumberOfNonCategoryBuckets =
    localDataUnclamped.numberOfBuckets === undefined ||
    yAxisValue === YAxisValues.Config_Categories ||
    semanticLegendSettingsLoading
      ? undefined
      : Math.max(
          MIN_NUMBER_OF_BUCKETS,
          Math.min(
            localDataUnclamped.numberOfBuckets ?? 0,
            MAX_NUMBER_OF_BUCKETS
          )
        );
  const localData = useMemo(() => {
    if (localNumberOfCategories === undefined) {
      if (localNumberOfNonCategoryBuckets === undefined) {
        return localDataUnclamped;
      }
      return {
        ...localDataUnclamped,
        numberOfBuckets: localNumberOfNonCategoryBuckets,
      };
    }
    return {
      ...localDataUnclamped,
      numberOfBuckets: localNumberOfCategories,
    };
  }, [
    localDataUnclamped,
    localNumberOfCategories,
    localNumberOfNonCategoryBuckets,
  ]);

  const updateOptimistically = useCallback(
    (newData: Partial<SemanticLegendSettingsWithCategoricalConfig>) => {
      // Only some fields can be updated optimistically. Others, we need to wait for the server to update.
      const optimisticData = _.pick(newData, OPTIMISTIC_UPDATE_FIELDS);
      setLocalData(prev => ({...prev, ...optimisticData}));
      debouncedUpdate(newData);
    },
    [debouncedUpdate]
  );

  // We need to know whether to display a loading state for the buckets. If the local data is out of sync
  // with the server data, then that means there is a mutation in progress that is affecting the buckets.
  const isLoadingBuckets = useMemo(() => {
    if (!canSaveWorkspace) {
      // if the user can't save the workspace, then the trueData will never be updated, so we can't
      // expect the local data to ever be in sync with the server data
      return semanticLegendSettingsLoading;
    }
    const fieldsToCompare = (
      Object.keys(
        localData
      ) as (keyof SemanticLegendSettingsWithCategoricalConfig)[]
    ).filter(key => localData[key] !== undefined); // filter out undefined fields because they won't override the trueData anyway
    return (
      semanticLegendSettingsLoading ||
      !_.isEqual(
        _.pick(localData, fieldsToCompare),
        _.pick(trueData, fieldsToCompare)
      )
    );
  }, [localData, trueData, semanticLegendSettingsLoading, canSaveWorkspace]);

  const combinedSemanticLegendSettings = useMemo(
    () => ({
      ...DEFAULT_SEMANTIC_LEGEND_SETTINGS,
      ...trueData,
      ...localData,
    }),
    [trueData, localData]
  );

  const semanticLegendPalette =
    useMaybeWorkspaceSettingsContext()?.semanticLegendPalette;

  const categoryRunColorMap = useMemo(() => {
    return getRunColorMap(
      combinedSemanticLegendSettings,
      configValuesForRunCategories,
      semanticLegendPalette
    );
  }, [
    combinedSemanticLegendSettings,
    configValuesForRunCategories,
    semanticLegendPalette,
  ]);

  const bucketsWithColors = useMemo(() => {
    const buckets =
      combinedSemanticLegendSettings.buckets ??
      recalculateSemanticLegendBuckets(
        combinedSemanticLegendSettings.numberOfBuckets,
        combinedSemanticLegendSettings.metricStatistics,
        combinedSemanticLegendSettings.yAxisValue,
        semanticLegendPalette ?? SEMANTIC_LEGEND_PALETTE_DEFAULT
      );
    return buckets.map((bucket, index) => ({
      ...bucket,
      color: getColorFromValue(
        index,
        buckets.length,
        semanticLegendPalette ?? SEMANTIC_LEGEND_PALETTE_DEFAULT
      ),
    }));
  }, [combinedSemanticLegendSettings, semanticLegendPalette]);

  useGroupingToastNotification(
    hasActiveGrouping,
    semanticLegendRunColorOptions
  );

  const value = useMemo(
    () => ({
      clearSetting,
      hasActiveGrouping,
      isMetricBasedColors: isKeyBasedColors,
      configKeys,
      semanticLegendRunColorOptions,
      semanticLegendSettings: {
        ...combinedSemanticLegendSettings,
        categoryRunColorMap,
        buckets: bucketsWithColors,
      },
      settingLevel,
      settingOverrideCounts,
      specPreviouslyCreated,
      trackSemanticLegendSettings,
      updateSemanticLegendRunColorOptions,
      updateSemanticLegendSettings: updateOptimistically,
      isLoadingBuckets,
    }),
    [
      clearSetting,
      hasActiveGrouping,
      isKeyBasedColors,
      combinedSemanticLegendSettings,
      categoryRunColorMap,
      configKeys,
      semanticLegendRunColorOptions,
      settingLevel,
      settingOverrideCounts,
      specPreviouslyCreated,
      trackSemanticLegendSettings,
      updateSemanticLegendRunColorOptions,
      updateOptimistically,
      isLoadingBuckets,
      bucketsWithColors,
    ]
  );

  return (
    <SemanticLegendSettingsWithCategoricalConfigContext.Provider value={value}>
      {children}
    </SemanticLegendSettingsWithCategoricalConfigContext.Provider>
  );
};

export const useSemanticLegendSettingsContext =
  (): SemanticLegendSettingsWithCategoricalConfigContextType => {
    const value = useContext(
      SemanticLegendSettingsWithCategoricalConfigContext
    );

    if (value == null) {
      return {
        clearSetting: () => {},
        hasActiveGrouping: false,
        configKeys: [],
        semanticLegendRunColorOptions: RunColorOptions.OriginalProjectColors,
        semanticLegendSettings: DEFAULT_SEMANTIC_LEGEND_SETTINGS,
        isMetricBasedColors: false,
        settingLevel: 'workspace',
        settingOverrideCounts: {},
        specPreviouslyCreated: false,
        trackSemanticLegendSettings: () => {},
        updateSemanticLegendRunColorOptions: () => {},
        updateSemanticLegendSettings: () => {},
        isLoadingBuckets: true,
      };
    }

    return value;
  };
